import { useEffect, useRef, useState } from 'react';

import {
  type StartTelegramLoginRequest,
  type SubmitTelegramCodeRequest,
  type SubmitTelegramPasswordRequest,
  type TelegramLoginStep,
  TelegramLoginStepSchema,
} from '../../../api';
import { type ApiError, toApiError } from '../../../lib/api/api-client';
import { useApiClient } from '../../../lib/api/api-context';
import { newIdempotencyKey } from '../../../lib/api/idempotency-key';
import { useTelegram } from '../../../lib/telegram/telegram-context';

import type { LoginView, Notice } from './login-view';
import { normalizePhone } from './phone';

/** What {@link useTelegramLogin} gives the screen. */
export interface TelegramLogin {
  readonly view: LoginView;
  /** A request is running: Confirm and "Get a new code" are disabled (R20). */
  readonly pending: boolean;
  /** Epoch ms until which "Get a new code" stays disabled (Telegram's delay), or `null`. */
  readonly newCodeBlockedUntil: number | null;
  readonly sharePhone: () => void;
  readonly typePhone: () => void;
  readonly submitPhone: (input: string) => void;
  readonly submitCode: (code: string) => void;
  readonly submitPassword: (password: string) => void;
  readonly newCode: () => void;
}

const MS_PER_MINUTE = 60_000;

function noticeOf(error: ApiError): Notice {
  return { reasonCode: error.reasonCode, params: error.params };
}

/** Telegram's delay as a deadline; `null` without a usable `params.minutes` (R17). */
function deadlineOf(error: ApiError): number | null {
  if (error.reasonCode !== 'TELEGRAM_CODE_RATE_LIMITED') return null;
  const minutes = error.params?.['minutes'];
  return typeof minutes === 'number' && Number.isSafeInteger(minutes) && minutes > 0
    ? Date.now() + minutes * MS_PER_MINUTE
    : null;
}

/**
 * The login of E2 (specs/S4-telegram-login/spec.md §4): the state machine, the three calls and one
 * `Idempotency-Key` per user action (R20).
 *
 * The code and the password are arguments of one call and are never stored here (R19, I9). The
 * phone number is kept in a ref once Telegram accepted it, because every `start` needs it ("Get a
 * new code"); it is never rendered, and it is dropped on success, on `TELEGRAM_ACCOUNT_MISMATCH`
 * and on unmount (Proposal P5). `onLoggedIn` is read through a ref so a new function each render
 * does not matter.
 */
export function useTelegramLogin(options: { readonly onLoggedIn: () => void }): TelegramLogin {
  const api = useApiClient();
  const telegram = useTelegram();
  const [view, setView] = useState<LoginView>({ step: 'phone', mode: 'choose', notice: null });
  const [pending, setPending] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const phone = useRef<string | null>(null);
  const busy = useRef(false);
  const sharing = useRef(false);
  const mounted = useRef(true);
  const onLoggedIn = useRef(options.onLoggedIn);

  useEffect(() => {
    onLoggedIn.current = options.onLoggedIn;
  });
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      phone.current = null;
    };
  }, []);
  // R17: the new-code link comes back when Telegram's delay has passed; the timer dies with E2.
  useEffect(() => {
    if (blockedUntil === null) return undefined;
    const timer = setTimeout(
      () => {
        setBlockedUntil(null);
      },
      Math.max(0, blockedUntil - Date.now()),
    );
    return () => {
      clearTimeout(timer);
    };
  }, [blockedUntil]);

  function show(step: TelegramLoginStep): void {
    switch (step.step) {
      case 'CODE_SENT':
        setBlockedUntil(null);
        setView({
          step: 'code',
          phoneMasked: step.phoneMasked,
          codeLength: step.codeLength,
          notice: null,
        });
        return;
      case 'PASSWORD_REQUIRED':
        setView({ step: 'password', notice: null });
        return;
      case 'LOGGED_IN':
        phone.current = null;
        onLoggedIn.current();
    }
  }

  function fail(error: ApiError): void {
    const notice = noticeOf(error);
    const deadline = deadlineOf(error);
    if (deadline !== null) setBlockedUntil(deadline);
    if (error.reasonCode === 'TELEGRAM_ACCOUNT_MISMATCH') {
      // R5, R19: the login ended on the server; forget the number and start again.
      phone.current = null;
      setView({ step: 'phone', mode: 'choose', notice });
      return;
    }
    if (error.reasonCode === 'TELEGRAM_PHONE_NUMBER_INVALID') {
      setView({ step: 'phone', mode: 'typed', notice });
      return;
    }
    setView((current) => ({ ...current, notice }));
  }

  /**
   * One user action: one request at a time, no state update after unmount. `remember` is the
   * number a `start` sent: it is kept only once Telegram accepted it.
   */
  async function run(request: () => Promise<TelegramLoginStep>, remember?: string): Promise<void> {
    if (busy.current) return;
    busy.current = true;
    setPending(true);
    try {
      const step = await request();
      if (mounted.current) {
        if (remember !== undefined) phone.current = remember;
        show(step);
      }
    } catch (error) {
      if (mounted.current) fail(toApiError(error));
    } finally {
      busy.current = false;
      if (mounted.current) setPending(false);
    }
  }

  function start(number: string): Promise<void> {
    // R20: a new action, a new key; a replay after a token renewal keeps it (the client's job).
    const idempotencyKey = newIdempotencyKey();
    const body: StartTelegramLoginRequest = { phoneNumber: number };
    return run(
      () =>
        api.post(
          '/telegram/login/start',
          { guard: { kind: 'authenticated', idempotencyKey }, body },
          TelegramLoginStepSchema,
        ),
      number,
    );
  }

  return {
    view,
    pending,
    newCodeBlockedUntil: blockedUntil,
    sharePhone: () => {
      if (busy.current || sharing.current) return;
      sharing.current = true;
      void telegram.requestPhoneNumber().then((number) => {
        sharing.current = false;
        if (!mounted.current) return;
        if (number === null) setView({ step: 'phone', mode: 'typed', notice: null });
        else void start(number);
      });
    },
    typePhone: () => {
      setView({ step: 'phone', mode: 'typed', notice: null });
    },
    submitPhone: (input) => {
      const number = normalizePhone(input);
      if (number === null) {
        setView({
          step: 'phone',
          mode: 'typed',
          notice: { reasonCode: 'TELEGRAM_PHONE_NUMBER_INVALID', params: undefined },
        });
        return;
      }
      void start(number);
    },
    submitCode: (code) => {
      const idempotencyKey = newIdempotencyKey();
      const body: SubmitTelegramCodeRequest = { code };
      void run(() =>
        api.post(
          '/telegram/login/code',
          { guard: { kind: 'authenticated', idempotencyKey }, body },
          TelegramLoginStepSchema,
        ),
      );
    },
    submitPassword: (password) => {
      const idempotencyKey = newIdempotencyKey();
      const body: SubmitTelegramPasswordRequest = { password };
      void run(() =>
        api.post(
          '/telegram/login/password',
          { guard: { kind: 'authenticated', idempotencyKey }, body },
          TelegramLoginStepSchema,
        ),
      );
    },
    newCode: () => {
      if (phone.current === null) setView({ step: 'phone', mode: 'choose', notice: null });
      else void start(phone.current);
    },
  };
}
